import {
  Button, Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  makeStyles, MenuItem, Select,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {TransitionProps} from '@material-ui/core/transitions/transition';
import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import Cube, {DifficultyType, GameType, JourneyPhase, LengthOfGame, NumberOfPlayers} from '../../../Domain/Models/Cube';
import Loading from "../../UiComponents/Loading";
import GamePointsSettings, {PointsPerLife} from "../../../Domain/Models/GamePointsSettings";
import {cubeRepository} from "../../../Domain/Repositories/CubeRepository";
import {gameRepository} from "../../../Domain/Repositories/GameRepository";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  }
}));

export interface GamePointsModalProps
{
  open: boolean;
  onClose: () => void;
  gamePointsSettings: GamePointsSettings;
  pointsPerLife: PointsPerLife;
  gameIndex: number
}

export default function GamePointsModal(props: GamePointsModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, onClose, pointsPerLife, gameIndex, gamePointsSettings} = props;
  const classes = useStyles();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const formRef = useRef<FormikProps<PointsPerLife>>(null);

  const schema = yup.object().shape({
    threeLives: yup.number().required('Required'),
    twoLives: yup.number().required('Required'),
    oneLife: yup.number().required('Required'),
  });

  const handleSubmit = (value: PointsPerLife) =>
  {
    
    console.debug("Submitting")
    setSubmitting(true);
    setSubmitError(undefined);
    
    gamePointsSettings.gameScores[gameIndex] = value;

    gameRepository.updateGamePoints(gamePointsSettings)
      .then(() => onClose())
      .catch((e: Error) =>
      {
        setSubmitting(false);
        setSubmitError(e.message);
      });
  }

  useEffect(() =>
  {
    if (open)
    {
      setSubmitError(undefined);
      setSubmitting(false);
    }
  }, [open]);

  return (
      <Dialog
          classes={{scrollPaper: classes.dialog}}
          open={open}
          onClose={onClose}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="md"
          disableEscapeKeyDown
          disableBackdropClick={submitting}
          TransitionComponent={Transition}
      >
        <DialogTitle>{`Game ${gameIndex + 1}`}</DialogTitle>

        <Loading isLoading={submitting}>
          <DialogContent>
            <Formik<PointsPerLife>
                initialValues={pointsPerLife}
                onSubmit={(value) => handleSubmit(value)}
                validationSchema={schema}
                innerRef={formRef}
            >
              {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur
                }) => (
                  <>
                    <FormControl fullWidth margin="normal" error={touched.threeLives && errors.threeLives !== undefined}>
                      <InputLabel htmlFor="component-error">Three Lives</InputLabel>
                      <Input
                        name="threeLives"
                        value={values.threeLives}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.threeLives && errors.threeLives}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.twoLives && errors.twoLives !== undefined}>
                      <InputLabel htmlFor="component-error">Two Lives</InputLabel>
                      <Input
                        name="twoLives"
                        value={values.twoLives}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.twoLives && errors.twoLives}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.oneLife && errors.oneLife !== undefined}>
                      <InputLabel htmlFor="component-error">One Life</InputLabel>
                      <Input
                        name="oneLife"
                        value={values.oneLife}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.oneLife && errors.oneLife}</FormHelperText>
                    </FormControl>
                  </>
              )}
            </Formik>

            {submitError != undefined && (
                <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
                  {submitError}
                </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button
                onClick={onClose} color="primary"
            >
              Cancel
            </Button>
            <Button
                onClick={() => formRef.current?.handleSubmit()}
                color="primary"
            >
              Submit
            </Button>
          </DialogActions>

        </Loading>
      </Dialog>
  );
}